// emailListener.js

import React, { useEffect } from 'react';
import io from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import config from './../configs';
import toast from 'react-hot-toast';
import { getAccessToken } from '../services/authService';
import { refreshAccessToken } from './fetchHelper';

// Mounts a Socket.IO connection only when the user is logged in, and passes the
// access token in the handshake so the backend can reject anonymous connections.
// Previously this connected unconditionally on app mount, leaking newEmail toasts
// to anyone landing on /session/login (and to other users on shared machines).
const EmailListener = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state) => state && state.authReducer && state.authReducer.isLoggedIn);

    useEffect(() => {
        if (!isLoggedIn) return undefined;
        const token = getAccessToken();
        if (!token) return undefined;

        const socket = io(config[config.env].baseURL, {
            path: '/socket.io',
            // Function form is re-invoked on every (re)connect, so a rotated access token is picked
            // up automatically instead of pinning the token captured at mount.
            auth: (cb) => cb({ token: getAccessToken() }),
        });

        // A handshake the server rejects (e.g. an access token that expired while the page sat open,
        // then a server restart or laptop sleep forced a reconnect) is final in socket.io: the client
        // never retries on its own, so every live update stayed dead until a manual page reload.
        // Refresh the token and reconnect, a few times at most.
        let authRetries = 0;
        socket.on('connect_error', async (err) => {
            console.warn('socket connect_error:', err && err.message);
            if (socket.active || authRetries >= 3) return; // socket.active: the client is already retrying
            authRetries += 1;
            try {
                await refreshAccessToken();
            } catch (_) {
                return; // refresh token gone too — the next API call's 401 handling takes over
            }
            setTimeout(() => socket.connect(), 1000 * authRetries);
        });

        // Events sent while the socket was down are lost — after a reconnect, refresh once (the
        // pages that react to new emails re-fetch on this counter).
        let connectedBefore = false;
        socket.on('connect', () => {
            authRetries = 0;
            if (connectedBefore) dispatch({ type: 'EMAIL_RECEIVED' });
            connectedBefore = true;
        });

        socket.on('initialEmails', (initialEmails) => {
            for (const email of initialEmails) {
                toast.success(`Initial Email from: ${email.from && email.from.text}`, {
                    position: 'top-right',
                });
            }
        });

        socket.on('newEmail', (email) => {
            toast.success(`New Email from: ${email.from && email.from.text}`, {
                position: 'top-right',
            });
            dispatch({ type: 'EMAIL_RECEIVED' });
        });

        socket.on('emailQueueUpdate', (payload) => {
            dispatch({ type: 'EMAIL_QUEUE_UPDATE', payload });
        });

        // The server changed supplier data in the background (a bounce, an automatic follow-up or
        // status change) — the Suppliers page and project overview refresh in place.
        socket.on('suppliersUpdated', () => {
            window.dispatchEvent(new CustomEvent('suppliers-imported'));
        });

        return () => {
            socket.disconnect();
        };
    }, [dispatch, isLoggedIn]);

    return null;
};

export default EmailListener;
